// WP-C1.7 (ST-4, SE-1): app-lock PIN — idle-timeout + manual "Lock now," a full-screen client
// UI gate on an ALREADY-authenticated session. This is NOT a second server-side auth boundary
// (the real one is the httpOnly session cookie, SE-3) — verification itself always happens
// server-side (POST /api/v1/auth/pin/verify), so the PIN comparison and lockout counter never
// run client-side; this provider only ever decides whether to SHOW the overlay.
import { appLockStatusResponseSchema, type AppLockStatusResponse } from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api, ApiError } from './api';
import { useSettings } from './settings';

export const PIN_STATUS_KEY = ['pinStatus'] as const;
const LOCKED_STORAGE_KEY = 'pfos.appLocked';
// ST-4/SE-1: "auto-lock after configurable idle time" — this is the default until the owner
// picks one in Settings > Security; 0 means manual-lock-only.
const DEFAULT_AUTO_LOCK_MINUTES = 5;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const;

export function usePinStatus(): UseQueryResult<AppLockStatusResponse> {
  return useQuery({
    queryKey: PIN_STATUS_KEY,
    queryFn: async () =>
      appLockStatusResponseSchema.parse(await api.get('/api/v1/auth/pin/status')),
  });
}

export function useSetPin(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pin: string) => {
      await api.post('/api/v1/auth/pin/set', { pin });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PIN_STATUS_KEY });
    },
  });
}

export function useClearPin(): UseMutationResult<void, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post('/api/v1/auth/pin/clear');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PIN_STATUS_KEY });
    },
  });
}

/** Verification only — the caller decides what "unlocked" means (AppLockOverlay calls
 * useAppLock().unlock() itself on success, not this hook). */
export function useVerifyPin(): UseMutationResult<void, ApiError, string> {
  return useMutation({
    mutationFn: async (pin: string) => {
      await api.post('/api/v1/auth/pin/verify', { pin });
    },
  });
}

interface AppLockContextValue {
  /** Whether a PIN is actually set (mirrors usePinStatus, exposed here so callers needn't
   * fetch it a second time). */
  enabled: boolean;
  locked: boolean;
  lockNow: () => void;
  /** Called by AppLockOverlay once the server confirms the PIN was correct. */
  unlock: () => void;
}

const AppLockContext = createContext<AppLockContextValue | null>(null);

export function AppLockProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { data: status } = usePinStatus();
  const { data: settingsData } = useSettings();
  const enabled = status?.enabled ?? false;
  const autoLockMinutes =
    typeof settingsData?.appLockAutoLockMinutes === 'number'
      ? settingsData.appLockAutoLockMinutes
      : DEFAULT_AUTO_LOCK_MINUTES;

  // Read from sessionStorage synchronously (not gated on `enabled`, which is still `undefined`
  // on the very first render) — a reload while locked must stay locked immediately, not flash
  // the app open until the pin-status query resolves.
  const [locked, setLocked] = useState(() => sessionStorage.getItem(LOCKED_STORAGE_KEY) === '1');
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const lockNow = useCallback(() => {
    setLocked(true);
    sessionStorage.setItem(LOCKED_STORAGE_KEY, '1');
  }, []);

  const unlock = useCallback(() => {
    setLocked(false);
    sessionStorage.removeItem(LOCKED_STORAGE_KEY);
  }, []);

  useEffect(() => {
    if (!enabled || autoLockMinutes <= 0 || locked) return;
    const resetTimer = (): void => {
      if (idleTimer.current !== undefined) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(lockNow, autoLockMinutes * 60_000);
    };
    resetTimer();
    for (const evt of ACTIVITY_EVENTS) window.addEventListener(evt, resetTimer);
    return () => {
      if (idleTimer.current !== undefined) clearTimeout(idleTimer.current);
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, resetTimer);
    };
  }, [enabled, autoLockMinutes, locked, lockNow]);

  // App lock was turned off (PIN cleared) while a stale lock was still armed — never leave the
  // owner stuck behind a PIN prompt for a feature that's no longer on.
  useEffect(() => {
    if (!enabled && locked) unlock();
  }, [enabled, locked, unlock]);

  const value = useMemo(
    () => ({ enabled, locked, lockNow, unlock }),
    [enabled, locked, lockNow, unlock],
  );

  return <AppLockContext.Provider value={value}>{children}</AppLockContext.Provider>;
}

export function useAppLock(): AppLockContextValue {
  const ctx = useContext(AppLockContext);
  if (ctx === null) throw new Error('useAppLock must be used within an AppLockProvider');
  return ctx;
}
